import React, { useState } from 'react'
import { Organisation, OrganisationSize, OrganisationType, SNI } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { useContext, useEffect } from 'react'
import { Button, makeStyles, tokens } from '@fluentui/react-components'
import { ModalActions } from '../common/modal/Modal'
import { t } from '../../i18n/i18n'
import { Grid } from '../common/grid/Grid'
import { useForm } from 'react-hook-form'
import { api } from '../../helpers/apiHelper'
import { IDropdownOption } from '../../interfaces/IDropdownOptions'
import { IOpenCloseModalProps } from '../../interfaces/IOpenCloseModalProps'
import { handleMessage } from '../../helpers/stateHelper'
import { OrganisationDetailsForm } from './OrganisationDetailsForm'
import { DraggableModal } from '../common/modal/DraggableModal'
import { Form } from '../common/form/Form'

const useStyles = makeStyles({
   marginTop: {
      marginTop: tokens.spacingVerticalXL,
   },
})

export const ModalOrganisationDetails = ({ open, closeModal }: IOpenCloseModalProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { setRootState } = dataContext
   const { organisation, hasMsalToken } = dataContext.state
   const organisationForm = useForm<Organisation>({})
   const {
      reset,
      handleSubmit,
      formState: { isDirty, isSubmitting },
   } = organisationForm
   const [orgSizes, setOrgSizes] = useState<IDropdownOption[]>([])
   const [orgTypes, setOrgTypes] = useState<IDropdownOption[]>([])
   const [snis, setSnis] = useState<SNI[]>([])

   useEffect(() => {
      if (!open) return

      const fetchData = async () => {
         try {
            const orgSizeResponse = await api().getAllOrganisationSizes()
            const orgTypesResponse = await api().getAllOrganisationTypes()
            const allSnisResponse = await api().getAllSnis()

            const sortedOrgSizes = orgSizeResponse.data.sort((a, b) => {
               const valueA = a.text.includes('+') ? Number(a.text.split('+')[0]) : Number(a.text.split('-')[0])
               const valueB = b.text.includes('+') ? Number(b.text.split('+')[0]) : Number(b.text.split('-')[0])
               return valueA - valueB
            })

            const mappedOrgSizes: IDropdownOption[] = sortedOrgSizes.map((size: OrganisationType) => ({
               key: size.id,
               text: size.text,
            }))

            const mappedOrgTypes: IDropdownOption[] = orgTypesResponse.data.map((type: OrganisationSize) => ({
               key: type.id,
               text: type.text,
            }))

            setOrgSizes(mappedOrgSizes)
            setOrgTypes(mappedOrgTypes)
            setSnis(allSnisResponse.data)
         } catch (error) {
            handleMessage(setRootState, 'error', t('SomethingWentWrong'), t('FetchFailed'))
         }
      }

      fetchData()
   }, [hasMsalToken, open, organisation, setRootState])

   useEffect(() => {
      if (organisation) {
         reset(organisation)
      }
   }, [organisation, reset])

   const closeAndReset = () => {
      closeModal()
      reset(organisation)
   }

   const onSubmit = async (data: Organisation) => {
      try {
         const response = await api().updateOrganisation(data)
         setRootState((prev) => ({
            ...prev,
            organisation: response.data,
         }))
         closeAndReset()
      } catch (error) {
         handleMessage(setRootState, 'error', t('SomethingWentWrong'), t('CouldNotUpdateItem', { item: t('Organisation') }))
      }
   }
   return (
      <>
         <DraggableModal open={open} title={t('OrganisationDetails')} onClose={closeAndReset}>
            <Form onSubmit={handleSubmit(onSubmit)}>
               <Grid gap="L">
                  <OrganisationDetailsForm
                     organisationForm={organisationForm}
                     orgSizes={orgSizes}
                     orgTypes={orgTypes}
                     snis={snis}
                     isSandbox={organisation.isSandbox}
                  />
               </Grid>
               {organisation.isSandbox && (
                  <ModalActions className={classes.marginTop}>
                     <Button onClick={closeAndReset} disabled={isSubmitting}>
                        {isDirty ? t('Close') : t('Cancel')}
                     </Button>

                     <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                        {t('Save')}
                     </Button>
                  </ModalActions>
               )}
            </Form>
         </DraggableModal>
      </>
   )
}
